import { useCallback, useEffect, useMemo, useState } from 'react';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Button, CircularProgress } from '@mui/material';
import {
  ActivityService,
  InvoiceService,
  TaxRateService,
  UnitService,
} from 'api';
import { ActivitiesParams, Activity } from 'api/activities/activities.types';
import { ResStatus } from 'api/api.types';
import {
  useEditCorrectiveInvoice,
  useEditInvoice,
} from 'api/combinedRequests/invoices';
import { CurrencyQueryParams } from 'api/invoices/invoices.types';
import { TaxRate, TaxRatesParams } from 'api/taxRates/taxRates.types';
import { Unit, UnitsParams } from 'api/units/units.types';
import { StyledButton } from 'components/Buttons';
import {
  AutocompletePaginationAsync,
  StyledTextField,
} from 'components/Inputs';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { debounce, isString } from 'lodash';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import {
  CorrectiveInvoice,
  CurrencyCode,
  Invoice,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  isInvoiceAdministrative,
  isInvoiceCorrective,
  isForeignInvoice,
} from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { KeywordSelectorDialog } from '../InvoiceLineDetailsDialog/KeywordSelectorDialog';
import * as S from './InvoiceLineForm.css';
import { FormInputs } from './types';
import {
  calculateGrossValue,
  calculateNetValue,
  calculateTaxValue,
  createHandleChange,
  createHandleOnBlur,
  prepareAddCorrectiveInvoiceLinePayload,
  prepareAddInvoiceLinePayload,
  prepareInvoiceLineData,
  prepareUpdateInvoiceLinePayload,
  prepareUpdateCorrectiveInvoiceLinePayload,
} from './utils';

type Props = {
  invoice: Invoice | CorrectiveInvoice;
  onClose: () => void;
  refetchInvoiceData: FetchInvoiceData;
  isPrevious?: boolean;
  invoiceLineData?: InvoiceLine;
  isEditing?: boolean;
};

export const InvoiceLineForm = ({
  invoice,
  onClose,
  refetchInvoiceData,
  isPrevious,
  invoiceLineData,
  isEditing,
}: Props) => {
  const { exchangeRateDate, invoiceClass } = invoice;
  const isNP = useMemo(() => invoiceClass.type === 'NO_VAT', [invoiceClass]);
  const isCorrective = useMemo(
    () => isInvoiceCorrective(invoiceClass),
    [invoiceClass],
  );
  const isForeignWithoutOwner = useMemo(
    () => isForeignInvoice(invoiceClass) && !invoice.owner,
    [invoice],
  );

  const { t } = useTranslation(['misc', 'invoice', 'currency', 'validation']);

  const defaultValues = useMemo(
    () => ({
      activity: invoiceLineData?.activity || null,
      description: invoiceLineData?.description || '',
      quantity: invoiceLineData?.quantity || null,
      unit: invoiceLineData?.unit || null,
      value: invoiceLineData?.currencyAmount.value || 0,
      currencyCode: invoiceLineData?.currencyAmount.currencyCode || null,
      exchangeRate: invoiceLineData?.exchangeRate || 1,
      netValue: invoiceLineData?.amounts.netValue || 0,
      taxRate: invoiceLineData?.amounts.taxRate || null,
      taxValue: invoiceLineData?.amounts.taxValue || 0,
      grossValue: invoiceLineData?.amounts.grossValue || 0,
      vehicleMileage: invoiceLineData?.vehicleMileage || null,
    }),
    [invoiceLineData],
  );

  const {
    control,
    register,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues,
  });
  const [isCalculating, setIsCalculating] = useState(true);
  const [isKeywordSelectorOpen, setIsKeywordSelectorOpen] = useState(false);
  const [selectedKeywordIndices, setSelectedKeywordIndices] = useState<
    number[] | null
  >(null);

  const getModifiedOriginalExternalName = () => {
    if (
      !invoiceLineData?.originalExternalName ||
      selectedKeywordIndices === null
    ) {
      return invoiceLineData?.originalExternalName;
    }
    const words = invoiceLineData.originalExternalName.split(' ');
    const selected = words.filter((_, i) => selectedKeywordIndices.includes(i));
    if (selected.length === 0) return invoiceLineData.originalExternalName;
    return `|${selected.join('|')}|`;
  };

  const renderNameWithPipes = (
    name: string | null | undefined,
  ): React.ReactNode => {
    if (!name) return null;
    if (!name.includes('|')) return name;
    return name.split(/\|+/).reduce<React.ReactNode[]>((acc, part, i) => {
      if (i > 0)
        acc.push(
          <S.BlankRect
            key={`b${i}`}
            style={acc.length === 0 ? { marginLeft: 0 } : undefined}
          />,
        );
      if (part) acc.push(<span key={`t${i}`}>{part}</span>);
      return acc;
    }, []);
  };

  const [activitiesOptions, setActivitiesOptions] = useState<Activity[]>([]);
  const [activitiesParams, setActivitiesParams] = useState<ActivitiesParams>({
    pageNumber: 1,
    resultSize: 25,
    type: isInvoiceAdministrative(invoice.invoiceClass)
      ? 'ADMINISTRATIVE'
      : 'NOT_ADMINISTRATIVE',
    direct: invoiceClass.own ? invoiceClass.own : undefined,
  });

  const [unitsOptions, setUnitsOptions] = useState<Unit[]>([]);
  const [unitsParams, setUnitsParams] = useState<UnitsParams>({
    pageNumber: 1,
    resultSize: 25,
    withSubUnits: true,
  });

  const [currenciesOptions, setCurrenciesOptions] = useState<CurrencyCode[]>(
    [],
  );
  const [currenciesParams, setCurrenciesParams] = useState<CurrencyQueryParams>(
    {
      pageNumber: 1,
      resultSize: 25,
      onlyVisible: true,
    },
  );

  const [taxRatesOptions, setTaxRatesOptions] = useState<TaxRate[]>([]);
  const [taxRatesParams, setTaxRatesParams] = useState<TaxRatesParams>({
    pageNumber: 1,
    resultSize: 25,
    filterText: isNP ? 'np' : '',
  });

  const {
    getActivities,
    loading: activitiesLoading,
    data: activitiesData,
  } = ActivityService.useGetActivities();

  const {
    getUnits,
    loading: unitsLoading,
    data: unitsData,
  } = UnitService.useGetUnits();

  const {
    getCurrencies,
    loading: currenciesLoading,
    currenciesData,
  } = InvoiceService.useGetCurrencies();

  const {
    getTaxRates,
    loading: taxRatesLoading,
    data: taxRatesData,
  } = TaxRateService.useGetTaxRates();

  const { getDefaultTaxRate } = TaxRateService.useGetDefaultTaxRate();

  const { getExchangeRateValue } = InvoiceService.useGetExchangeRateValue();

  const { editInvoice, loading: editInvoiceLoading } = useEditInvoice();
  const { editCorrectiveInvoice, loading: editCorrectiveInvoiceLoading } =
    useEditCorrectiveInvoice();

  useEffect(() => {
    if (activitiesData?.length) {
      const enabledActivities = activitiesData.filter(
        (activity) => activity.enabled,
      );
      setActivitiesOptions((prev) => [...prev, ...enabledActivities]);
    }
  }, [activitiesData]);

  useEffect(() => {
    getActivities({ ...activitiesParams });
  }, [getActivities, activitiesParams]);

  useEffect(() => {
    if (unitsData?.length) {
      const enabledUnits = unitsData.filter((unit) => unit.enabled);
      setUnitsOptions((prev) => [...prev, ...enabledUnits]);
    }
  }, [unitsData]);

  useEffect(() => {
    getUnits({ ...unitsParams });
  }, [getUnits, unitsParams]);

  useEffect(() => {
    if (taxRatesData?.length && !isNP) {
      const enabledTaxRates = taxRatesData.filter((taxRate) => taxRate.enabled);
      setTaxRatesOptions((prev) => [...prev, ...enabledTaxRates]);
    }
  }, [taxRatesData]);

  useEffect(() => {
    getTaxRates({ ...taxRatesParams }).then((res) => {
      if (isNP) {
        setValue('taxRate', res.data?.result?.data[0] as TaxRate);
        trigger('taxRate');
      }
    });
  }, [getTaxRates, taxRatesParams]);

  const getExcludedTaxRateCode = (
    invoice: Invoice | CorrectiveInvoice,
  ): string | null => {
    const classCode = invoice?.invoiceClass?.code;
    if (
      classCode !== 'FVS' &&
      classCode !== 'FNPS' &&
      classCode !== 'FKS' &&
      classCode !== 'FNPKS' &&
      !isNP
    )
      return null;

    const countryCode = invoice?.buyerCounterparty?.address?.countryCode;
    if (countryCode === 'PL') return 'NP';
    if (countryCode) return '0%';

    return null;
  };

  const excludedTaxRate = getExcludedTaxRateCode(invoice);

  const filteredTaxRatesOptions = excludedTaxRate
    ? taxRatesOptions.filter(
        (taxRate) => !(taxRate.code === excludedTaxRate && taxRate.enabled),
      )
    : taxRatesOptions;

  useEffect(() => {
    if (currenciesData?.length) {
      const enabledCurrencies = currenciesData.filter(
        (currency) => currency.enabled,
      );
      setCurrenciesOptions((prev) => [...prev, ...enabledCurrencies]);
    }
  }, [currenciesData]);

  useEffect(() => {
    getCurrencies({ ...currenciesParams });
  }, [getCurrencies, currenciesParams]);

  const activityValue: any = useWatch({
    control,
    name: 'activity',
    defaultValue: defaultValues.activity,
  });
  const quantityValue: any = useWatch({ control, name: 'quantity' });
  const valueValue: any = useWatch({ control, name: 'value' });
  const exchangeRateValue: any = useWatch({ control, name: 'exchangeRate' });
  const netValue: any = useWatch({ control, name: 'netValue' });
  const taxRateValue: any = useWatch({
    control,
    name: 'taxRate',
    defaultValue: defaultValues.taxRate,
  });
  const taxValue: any = useWatch({ control, name: 'taxValue' });
  const currencyCodeValue: any = useWatch({ control, name: 'currencyCode' });
  const grossValue: any = useWatch({ control, name: 'grossValue' });

  const selectOnFocus = (
    e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>,
  ) => e.target.select();

  const onSubmit = async (data: FormInputs) => {
    const invoiceLine = prepareInvoiceLineData(data);
    const externalName = getModifiedOriginalExternalName();
    let payload;
    if (isEditing) {
      payload = prepareUpdateInvoiceLinePayload(
        invoice,
        invoiceLineData!.id!,
        invoiceLine,
      );
    } else {
      payload = prepareAddInvoiceLinePayload(invoice, invoiceLine);
    }
    if (externalName) {
      payload = {
        ...payload,
        mappingRequest: {
          activityId: data.activity!.id,
          counterpartyId: invoice.sellerCounterparty.id,
          externalName,
          isPattern: externalName.includes('|'),
        },
      };
    }
    const res = await editInvoice(invoice, payload);
    !!res &&
      res.status === ResStatus.OK &&
      appToast.success(t('message.updateSuccess', { ns: 'misc' })) &&
      onClose();
    await refetchInvoiceData(invoice.id);
  };

  const onSubmitCorrective = async (data: FormInputs) => {
    const invoiceLine = prepareInvoiceLineData(data);
    const externalName = getModifiedOriginalExternalName();
    let payload;
    if (isEditing) {
      payload = prepareUpdateCorrectiveInvoiceLinePayload(
        invoice as CorrectiveInvoice,
        invoiceLineData!.id!,
        invoiceLine,
        isPrevious as boolean,
      );
    } else {
      payload = prepareAddCorrectiveInvoiceLinePayload(
        invoice as CorrectiveInvoice,
        invoiceLine,
        isPrevious as boolean,
      );
    }
    if (externalName) {
      payload = {
        ...payload,
        mappingRequest: {
          activityId: data.activity!.id,
          counterpartyId: invoice.sellerCounterparty.id,
          externalName,
          isPattern: externalName.includes('|'),
        },
      };
    }
    const res = await editCorrectiveInvoice(invoice, payload);
    !!res &&
      res.status === ResStatus.OK &&
      appToast.success(t('message.updateSuccess', { ns: 'misc' }));
    onClose();
    await refetchInvoiceData(invoice.id, isCorrective);
  };

  useEffect(() => {
    trigger(['vehicleMileage']);
    !isEditing &&
      activityValue?.id &&
      !isNP &&
      getDefaultTaxRate().then((res) => {
        setValue('taxRate', res.data?.result as TaxRate);
        trigger('taxRate');
      });
  }, [activityValue, isNP]);

  useEffect(() => {
    if (currencyCodeValue?.currency) {
      getExchangeRateValue({
        from: currencyCodeValue?.currency,
        to: invoice.currencyCode.currency,
        date: exchangeRateDate,
      }).then((res) => {
        const result = res.data.result;
        const exchangeRate = result.find(function (exchangeRate) {
          return exchangeRate.sourceCurrency === currencyCodeValue.currency;
        });
        if (exchangeRate) {
          setValue('exchangeRate', exchangeRate.rate);
          trigger('exchangeRate');
        }
      });
    }
  }, [currencyCodeValue?.currency]);

  const handleCalculateNetValue = useCallback(
    debounce((quantityValue, valueValue, exchangeRateValue) => {
      const result = calculateNetValue(
        quantityValue,
        valueValue,
        exchangeRateValue,
      );
      setValue('netValue', isNaN(result) ? 'nie jest liczbą' : result);
    }, 200),
    [],
  );

  useEffect(() => {
    setIsCalculating(true);
    handleCalculateNetValue(quantityValue, valueValue, exchangeRateValue);
  }, [quantityValue, valueValue, exchangeRateValue]);

  useEffect(() => {
    const result = calculateTaxValue(netValue, taxRateValue);
    setValue('taxValue', isNaN(result) ? 'nie jest liczbą' : result);
  }, [netValue, taxRateValue, setValue]);

  useEffect(() => {
    const result = calculateGrossValue(netValue, taxValue);
    setValue('grossValue', isNaN(result) ? 'nie jest liczbą' : result);
  }, [netValue, taxValue, setValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsCalculating(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [quantityValue, valueValue, exchangeRateValue]);

  useEffect(() => {
    trigger();
  }, []);

  return (
    <>
      <LoaderFullScreen
        loading={editInvoiceLoading || editCorrectiveInvoiceLoading}
      />
      <S.FormContainer
        onSubmit={handleSubmit(!isCorrective ? onSubmit : onSubmitCorrective)}
      >
        <S.FieldGroup>
          <S.FieldLabel htmlFor='activity'>
            {t('activity', { ns: 'invoice' })}
          </S.FieldLabel>
          <Controller
            name='activity'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              // NOTE: should be moved to different component (see InvoiceForm.tsx)
              <AutocompletePaginationAsync
                {...props}
                hiddenLabel
                padding='dense'
                margin='dense'
                width={280}
                disabled={invoiceLineData?.correctiveIsFromOriginal}
                error={!!errors.activity}
                loading={activitiesLoading}
                value={props.field.value}
                options={activitiesOptions}
                getOptionLabel={(option) => option.code}
                handleSearchChange={createHandleChange(
                  setActivitiesOptions,
                  setActivitiesParams,
                )}
                onScrollBottom={() =>
                  setActivitiesParams((prev) => ({
                    ...prev,
                    pageNumber: (prev.pageNumber! += 1),
                  }))
                }
                onBlur={createHandleOnBlur(
                  setActivitiesOptions,
                  setActivitiesParams,
                  props.field.onBlur,
                )}
                onChange={(_, data) => props.field.onChange(data)}
              />
            )}
          />
          <S.ErrorContainer>
            {errors.activity && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        {invoiceLineData?.activity.code === 'NIEROZPOZNANE' && (
          <S.FieldGroup>
            <S.FieldLabel htmlFor='originalExternalName'>
              {t('originalExternalName', { ns: 'invoice' })}
            </S.FieldLabel>
            <S.ExternalNameDisplay id='originalExternalName'>
              {selectedKeywordIndices === null
                ? renderNameWithPipes(invoiceLineData.originalExternalName)
                : renderNameWithPipes(getModifiedOriginalExternalName())}
            </S.ExternalNameDisplay>
            <S.ErrorContainer>
              <Button
                variant='contained'
                size='small'
                onClick={() => setIsKeywordSelectorOpen(true)}
                sx={{
                  minWidth: 'unset',
                  width: 35,
                  height: 35,
                  padding: 0,
                  marginLeft: 1,
                  backgroundColor: '#f2f2f2',
                  boxShadow: 'none',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 0, 0, 0.26)',
                  ':hover': { backgroundColor: '#e6e6e6', boxShadow: 'none' },
                }}
              >
                <FactCheckOutlinedIcon htmlColor='#989898' />
              </Button>
            </S.ErrorContainer>
          </S.FieldGroup>
        )}

        {invoiceLineData?.activity.code === 'NIEROZPOZNANE' &&
          invoiceLineData.originalExternalName && (
            <KeywordSelectorDialog
              open={isKeywordSelectorOpen}
              originalExternalName={invoiceLineData.originalExternalName}
              initialSelectedIndices={selectedKeywordIndices ?? []}
              onSave={(indices) => {
                setSelectedKeywordIndices(indices);
                setIsKeywordSelectorOpen(false);
              }}
              onClose={() => setIsKeywordSelectorOpen(false)}
            />
          )}

        <S.FieldGroup>
          <S.FieldLabel htmlFor='description'>
            {t('description', { ns: 'misc' })}
          </S.FieldLabel>
          <StyledTextField
            id='description'
            size='small'
            padding='dense'
            hiddenLabel
            margin='dense'
            width={280}
            disabled={isForeignWithoutOwner}
            error={!!errors.description}
            onFocus={selectOnFocus}
            {...register('description', {
              maxLength: {
                value: 256,
                message: t('maxLength', {
                  ns: 'validation',
                  replace: { '0': '256' },
                }),
              },
            })}
          />
          <S.ErrorContainer>
            {errors.description && (
              <S.Error>{errors?.description?.message}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='quantity'>
            {t('quantity', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='quantity'
            size='small'
            padding='dense'
            hiddenLabel
            margin='dense'
            width={280}
            // disabled={isForeignWithoutOwner}
            disabled={false}
            onFocus={selectOnFocus}
            error={!!errors.quantity}
            inputProps={{ maxLength: 15 }}
            {...register('quantity', {
              required: t('required', { ns: 'validation' }),
              min: {
                value: 0,
                message: t('min', { ns: 'validation', replace: { '0': '0' } }),
              },
              pattern: {
                value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                message: t('number', { ns: 'validation' }),
              },
              setValueAs: (v) => (isString(v) ? v?.replace(',', '.') : v),
            })}
          />
          <S.ErrorContainer>
            {errors.quantity && (
              <S.Error>
                {errors?.quantity?.message ||
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='unit'>
            {t('unit', { ns: 'misc' })}
          </S.FieldLabel>
          <Controller
            name='unit'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              // NOTE: should be moved to different component (see InvoiceForm.tsx)
              <AutocompletePaginationAsync
                {...props}
                padding='dense'
                hiddenLabel
                margin='dense'
                width={280}
                disabled={isForeignWithoutOwner}
                error={!!errors.unit}
                value={props.field.value}
                loading={unitsLoading}
                options={unitsOptions}
                getOptionLabel={(option) => option.code}
                handleSearchChange={createHandleChange(
                  setUnitsOptions,
                  setUnitsParams,
                )}
                onScrollBottom={() =>
                  setUnitsParams((prev) => ({
                    ...prev,
                    pageNumber: (prev.pageNumber! += 1),
                  }))
                }
                onBlur={createHandleOnBlur(
                  setUnitsOptions,
                  setUnitsParams,
                  props.field.onBlur,
                )}
                onChange={(_, data) => props.field.onChange(data)}
              />
            )}
          />
          <S.ErrorContainer>
            {errors.unit && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='value'>
            {t('price', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='value'
            size='small'
            padding='dense'
            hiddenLabel
            margin='dense'
            width={280}
            // disabled={isForeignWithoutOwner}
            disabled={false}
            onFocus={selectOnFocus}
            error={!!errors.value}
            inputProps={{ maxLength: 13 }}
            {...register('value', {
              required: 'To pole jest wymagane',
              ...(!isForeignWithoutOwner && {
                min: {
                  value: 0,
                  message: t('min', {
                    ns: 'validation',
                    replace: { '0': '0' },
                  }),
                },
              }),
              pattern: {
                value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                message: t('number', { ns: 'validation' }),
              },
              setValueAs: (v) => (isString(v) ? v?.replace(',', '.') : v),
            })}
          />
          <S.ErrorContainer>
            {errors.value && (
              <S.Error>
                {errors?.value?.message || t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='currencyCode'>
            {t('currency', { ns: 'currency' })}
          </S.FieldLabel>
          <Controller
            name='currencyCode'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              // NOTE: should be moved to different component (see InvoiceForm.tsx)
              <AutocompletePaginationAsync
                {...props}
                hiddenLabel
                padding='dense'
                margin='dense'
                width={280}
                disabled={isForeignWithoutOwner}
                error={!!errors.currencyCode}
                loading={currenciesLoading}
                value={props.field.value}
                options={currenciesOptions}
                getOptionLabel={(option) => option.currency}
                handleSearchChange={createHandleChange(
                  setCurrenciesOptions,
                  setCurrenciesParams,
                )}
                onScrollBottom={() =>
                  setCurrenciesParams((prev) => ({
                    ...prev,
                    pageNumber: (prev.pageNumber! += 1),
                  }))
                }
                onBlur={createHandleOnBlur(
                  setCurrenciesOptions,
                  setCurrenciesParams,
                  props.field.onBlur,
                )}
                onChange={(_, data) => props.field.onChange(data)}
              />
            )}
          />
          <S.ErrorContainer>
            {errors.currencyCode && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='exchangeRate'>
            {t('exchangeRate', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='exchangeRate'
            size='small'
            padding='dense'
            margin='dense'
            hiddenLabel
            disabled={isForeignWithoutOwner}
            error={!!errors.exchangeRate}
            width={280}
            onFocus={selectOnFocus}
            {...register('exchangeRate', {
              required: true,
              min: {
                value: 0,
                message: t('min', { ns: 'validation', replace: { '0': '0' } }),
              },
              pattern: {
                value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                message: t('number', { ns: 'validation' }),
              },
              setValueAs: (v) => (isString(v) ? v?.replace(',', '.') : v),
            })}
          />
          <S.ErrorContainer>
            {errors.exchangeRate && (
              <S.Error>
                {errors?.exchangeRate?.message ||
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='netValue'>
            {t('netValue', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='netValue'
            size='small'
            padding='dense'
            margin='dense'
            hiddenLabel
            disabled
            width={280}
            error={!!errors.netValue}
            {...register('netValue', { required: true })}
          />
          <S.ErrorContainer>
            {errors.netValue && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='taxRate'>
            {t('taxRate', { ns: 'invoice' })}
          </S.FieldLabel>
          <Controller
            name='taxRate'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              // NOTE: should be moved to different component (see InvoiceForm.tsx)
              <AutocompletePaginationAsync
                {...props}
                hiddenLabel
                padding='dense'
                margin='dense'
                width={280}
                error={!!errors.taxRate}
                defaultValue={invoiceLineData?.amounts.taxRate}
                value={props.field.value}
                loading={taxRatesLoading}
                options={filteredTaxRatesOptions}
                disabled={isNP}
                getOptionLabel={(option) => (option?.code ? option.code : '')}
                handleSearchChange={createHandleChange(
                  setTaxRatesOptions,
                  setTaxRatesParams,
                )}
                onScrollBottom={() =>
                  setTaxRatesParams((prev) => ({
                    ...prev,
                    pageNumber: (prev.pageNumber! += 1),
                  }))
                }
                onBlur={createHandleOnBlur(
                  setTaxRatesOptions,
                  setTaxRatesParams,
                  props.field.onBlur,
                )}
                onChange={(_, data) => props.field.onChange(data)}
              />
            )}
          />
          <S.ErrorContainer>
            {errors.taxRate && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='taxValue'>
            {t('taxValue', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='taxValue'
            size='small'
            padding='dense'
            margin='dense'
            hiddenLabel
            disabled
            width={280}
            {...register('taxValue', { required: true })}
          />
          <S.ErrorContainer>
            {errors.taxValue && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel htmlFor='grossValue'>
            {t('grossValue', { ns: 'invoice' })}
          </S.FieldLabel>
          <StyledTextField
            id='grossValue'
            size='small'
            padding='dense'
            hiddenLabel
            margin='dense'
            disabled
            width={280}
            {...register('grossValue', { required: true })}
          />
          <S.ErrorContainer>
            {errors.grossValue && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorContainer>
        </S.FieldGroup>

        {activityValue?.transportMeansSell && (
          <S.FieldGroup>
            <S.FieldLabel htmlFor='vehicleMileage'>
              {t('vehicleMileage', { ns: 'invoice' })}
            </S.FieldLabel>
            <StyledTextField
              id='vehicleMileage'
              size='small'
              padding='dense'
              hiddenLabel
              margin='dense'
              width={280}
              disabled={isForeignWithoutOwner}
              error={!!errors.vehicleMileage}
              {...register('vehicleMileage', {
                pattern: {
                  value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                  message: t('number', { ns: 'validation' }),
                },
                setValueAs: (v) =>
                  isString(v) ? parseFloat(v?.replace(',', '.')) : v,
              })}
            />
            <S.ErrorContainer>
              {errors.vehicleMileage && (
                <S.Error>{errors?.vehicleMileage?.message}</S.Error>
              )}
            </S.ErrorContainer>
          </S.FieldGroup>
        )}

        <S.SectionDivider />
        <S.ActionsContainer>
          <StyledButton
            $primary
            type='submit'
            disabled={
              !isValid ||
              isCalculating ||
              (isEditing &&
                activityValue?.code === 'NIEROZPOZNANE' &&
                activityValue.id === 84)
            }
            sx={{ minWidth: '73px' }}
          >
            {isCalculating ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              t('button.save', { ns: 'misc' })
            )}
          </StyledButton>
          <StyledButton onClick={onClose}>
            {t('button.cancel', { ns: 'misc' })}
          </StyledButton>
        </S.ActionsContainer>
      </S.FormContainer>
    </>
  );
};
